// src/components/MemberStandingFlags.js
//
// Bob, 2026-10-25: the Local's Secretary & Treasurer places a PRIVATE standing mark to INDICATE a concern to the
// member's Local Chairperson and General Chairperson. This is the LC/GC end of that - the read surface that lets the
// indication actually reach them. The S&T places and lifts the mark on the My Local Office page (src\pages\LocalOffice.js);
// this shows the marks that speak to THIS officer, and to no one else.
//
// A mark lives at memberStanding/{local}/marks/{markId} - keyed by the S&T's Local - but the LC and GC are scoped by
// scopeKey and committee, not by Local. The mark carries the member's own scopeKey and committee (stamped by the
// memberStandingMark function), and firestore.rules lets the member's LC read by scopeKey and his GC by committee. So the
// read is a collectionGroup over 'marks' filtered by the officer's own scope - which is exactly what the rules allow and
// no more. Never the member; nothing public.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collectionGroup, query, where, onSnapshot } from 'firebase/firestore';
import { capScopes } from '../data/scopeCap';
import { markLabel } from '../data/memberStanding';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', text: '#DCE7F2' };

export default function MemberStandingFlags({ user }) {
  const [byKey, setByKey] = useState({});   // listener key -> docs, merged for display
  const role = user && user.role;
  const committee = (user && user.committee) || '';
  const scopeKeys = (user && role === 'local_chair' && Array.isArray(user.scopes)) ? capScopes(user).scopes : [];
  const scopeKeyStr = scopeKeys.join(',');

  useEffect(() => {
    if (!user) return undefined;
    setByKey({});
    const keep = (m) => m && m.kind === 'mark' && m.state === 'marked';
    if (role === 'general_chair' && committee) {
      const q = query(collectionGroup(db, 'marks'), where('committee', '==', committee));
      return onSnapshot(q, (s) => setByKey({ gc: s.docs.map((d) => ({ id: d.id, ...d.data() })).filter(keep) }), () => setByKey({}));
    }
    if (role === 'local_chair' && scopeKeys.length) {
      const unsubs = scopeKeys.map((sk) => onSnapshot(
        query(collectionGroup(db, 'marks'), where('scopeKey', '==', sk)),
        (s) => setByKey((prev) => ({ ...prev, [sk]: s.docs.map((d) => ({ id: d.id, ...d.data() })).filter(keep) })),
        () => {}));
      return () => unsubs.forEach((u) => u());
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, committee, scopeKeyStr]);

  const seen = new Set();
  const marks = Object.values(byKey).flat().filter((m) => (seen.has(m.id) ? false : seen.add(m.id)))
    .sort((a, b) => (a.on < b.on ? 1 : a.on > b.on ? -1 : 0));
  if (!marks.length) return null;
  return (
    <div style={{ border: '1px solid ' + C.accent, background: C.panel, padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>Member standing flags ({marks.length})</div>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Placed by a Local's Secretary &amp; Treasurer to indicate a standing concern - working but not paying dues, or conduct harming the union or another member. Private to the S&amp;T, you, and the General Chairperson. Never shown to the member. It indicates; it decides nothing.</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>{marks.map((m) => (
        <li key={m.id} style={{ fontSize: 12, color: C.text, borderTop: '1px dotted ' + C.line, padding: '4px 0' }}>
          {(m.memberName || m.memberUid) + ' — ' + markLabel(m) + (m.local ? ' — Local ' + m.local : '')}
        </li>
      ))}</ul>
    </div>
  );
}
